import { AuthService } from './authService'

/**
 * Token Refresh Service
 * Automatically refreshes access tokens before they expire
 * 
 * Token Lifecycle:
 * - Access Token: 15 minutes
 * - Refresh Token: 7 days
 * - Auto-refresh: 2 minutes before expiry
 */

let refreshInterval: ReturnType<typeof setTimeout> | null = null
let isRefreshing = false

const REFRESH_BEFORE_EXPIRY = 2 * 60 * 1000 // Refresh 2 minutes before expiry

interface TokenPayload {
  exp: number
  iat: number
  userId: string
  email: string
  role: string
}

/**
 * Decode JWT token to get expiration time
 */
const decodeToken = (token: string): TokenPayload | null => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3 || !parts[1]) return null
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Failed to decode token:', error)
    return null
  }
}

/**
 * Get time until token expires (in milliseconds)
 */
const getTimeUntilExpiry = (token: string): number => {
  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) {
    return 0
  }
  
  const expiryTime = decoded.exp * 1000 // Convert to milliseconds
  const currentTime = Date.now()
  return expiryTime - currentTime
}

/**
 * Refresh the access token using the refresh token
 */
const refreshAccessToken = async (): Promise<boolean> => {
  if (isRefreshing) {
    console.log('Token refresh already in progress')
    return false
  }

  try {
    isRefreshing = true
    const refreshToken = localStorage.getItem('refreshToken')
    
    if (!refreshToken) {
      console.log('No refresh token available')
      return false
    }

    console.log('Refreshing access token...')
    
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://britfiler-backend-w5rjg.ondigitalocean.app/api'}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      console.error('Token refresh failed:', response.status)
      return false
    }

    const data = await response.json()
    const tokens = (data && data.response) ? data.response : data
    const newAccessToken = tokens?.accessToken
    const newRefreshToken = tokens?.refreshToken
    
    // Backend returns: { accessToken, refreshToken, user } or { response: { ... } }
    if (newAccessToken && newRefreshToken) {
      // Both tokens are rotated - store new tokens
      localStorage.setItem('accessToken', newAccessToken)
      localStorage.setItem('refreshToken', newRefreshToken)
      
      console.log('Tokens refreshed successfully (both rotated)')
      
      // Schedule next refresh
      scheduleTokenRefresh()
      
      return true
    }
    
    return false
  } catch (error) {
    console.error('Error refreshing token:', error)
    return false
  } finally {
    isRefreshing = false
  }
}

/**
 * Schedule the next token refresh
 */
const scheduleTokenRefresh = () => {
  // Clear existing interval
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }

  const accessToken = localStorage.getItem('accessToken')
  
  if (!accessToken) {
    console.log('No access token to schedule refresh')
    return
  }

  const timeUntilExpiry = getTimeUntilExpiry(accessToken)
  
  if (timeUntilExpiry <= 0) {
    console.log('Token already expired, refreshing immediately')
    refreshAccessToken()
    return
  }

  // Schedule refresh 2 minutes before token expires
  const refreshTime = Math.max(timeUntilExpiry - REFRESH_BEFORE_EXPIRY, 0)
  
  console.log(`Token will be refreshed in ${Math.round(refreshTime / 1000)} seconds`)
  
  refreshInterval = setTimeout(() => {
    refreshAccessToken()
  }, refreshTime)
}

/**
 * Start automatic token refresh
 * Call this after successful login
 */
export const startTokenRefresh = () => {
  console.log('Starting automatic token refresh')
  scheduleTokenRefresh()
}

/**
 * Stop automatic token refresh
 * Call this on logout
 */
export const stopTokenRefresh = () => {
  console.log('Stopping automatic token refresh')
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
  isRefreshing = false
}

/**
 * Check if tokens are valid and refresh if needed
 * Can be called on app initialization
 */
export const initializeTokenRefresh = async () => {
  const accessToken = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')
  
  if (!accessToken || !refreshToken) {
    console.log('No tokens found, skipping token refresh initialization')
    return
  }

  const timeUntilExpiry = getTimeUntilExpiry(accessToken)
  
  // If token expires in less than 5 minutes, refresh immediately
  if (timeUntilExpiry < 5 * 60 * 1000) {
    console.log('Access token expiring soon, refreshing immediately')
    const success = await refreshAccessToken()
    if (!success) {
      // If refresh fails, logout user
      console.log('Token refresh failed, logging out')
      AuthService.logout()
    }
  } else {
    // Schedule normal refresh
    scheduleTokenRefresh()
  }
}

/**
 * Manually trigger token refresh
 * Useful for testing or explicit refresh needs
 */
export const manualRefreshToken = async (): Promise<boolean> => {
  return await refreshAccessToken()
}

export default {
  startTokenRefresh,
  stopTokenRefresh,
  initializeTokenRefresh,
  manualRefreshToken,
}
